import React, { useState, useMemo } from 'react';
import { Search, X, AlertCircle, Flame, Sparkle, Sparkles, CircleSmall } from 'lucide-react';
import { AporteAnalysis, AporteFilters } from '../services/aporteService';
import { Select } from './ui/Select';
import { formatDate } from '../utils/formatters';
import { PillSelect } from './ui/PillSelect';
import { AporteMiembroDrawer } from './AporteMiembroDrawer';

const ESTADO_PAGO_LABEL: Record<'todos' | 'al_dia' | 'mora', string> = {
  todos: 'Todos',
  al_dia: 'Aportó',
  mora: 'No aportó',
};

interface AportesListaMiembrosProps {
  readonly análisis: AporteAnalysis[];
  readonly loading: boolean;
  readonly onFilterChange: (filters: AporteFilters) => void;
  readonly lumisialId: string;
  readonly puedeOperar: boolean;
}

export const AportesListaMiembros: React.FC<AportesListaMiembrosProps> = ({
  análisis,
  loading,
  onFilterChange,
  lumisialId,
  puedeOperar
}) => {
  const [drawerMiembro, setDrawerMiembro] = useState<AporteAnalysis | null>(null);
  const [drawerMiembroData, setDrawerMiembroData] = useState<AporteAnalysis | null>(null);

  const openDrawer = (miembro: AporteAnalysis) => {
    setDrawerMiembroData(miembro);
    setDrawerMiembro(miembro);
  };

  const closeDrawer = () => {
    setDrawerMiembro(null);
    setTimeout(() => setDrawerMiembroData(null), 300);
  };

  const [busqueda, setBusqueda] = useState('');
  const [estadoPago, setEstadoPago] = useState<'todos' | 'al_dia' | 'mora'>('todos');
  const [rachaMinima, setRachaMinima] = useState('');
  const [sinAportar, setSinAportar] = useState('');
  const [ordenarPor, setOrdenarPor] = useState<'nombre' | 'racha' | 'ultimo_aporte'>('nombre');

  const handleBusquedaChange = (value: string) => {
    setBusqueda(value);
    onFilterChange({ busqueda: value.trim() || undefined });
  };

  const resetFilters = () => {
    setBusqueda('');
    setEstadoPago('todos');
    setRachaMinima('');
    setSinAportar('');
    setOrdenarPor('nombre');
    onFilterChange({});
  };

  const hasActiveFilters = !!(busqueda || estadoPago !== 'todos' || rachaMinima || sinAportar);

  const análisisFiltrado = useMemo(() => {
    let result = [...análisis];
    if (estadoPago === 'al_dia') result = result.filter(a => a.aportio_este_mes);
    else if (estadoPago === 'mora') result = result.filter(a => !a.aportio_este_mes);
    if (rachaMinima) result = result.filter(a => a.racha_aporte_mensual >= Number(rachaMinima));
    if (sinAportar) result = result.filter(a => a.meses_sin_aportar >= Number(sinAportar));
    if (ordenarPor === 'nombre') {
      result.sort((a, b) => `${a.apellidos} ${a.nombres}`.toLowerCase().localeCompare(`${b.apellidos} ${b.nombres}`.toLowerCase(), 'es'));
    } else if (ordenarPor === 'racha') {
      result.sort((a, b) => b.racha_aporte_mensual - a.racha_aporte_mensual);
    } else if (ordenarPor === 'ultimo_aporte') {
      result.sort((a, b) => {
        if (!a.ultimo_aporte_fecha) return 1;
        if (!b.ultimo_aporte_fecha) return -1;
        return new Date(b.ultimo_aporte_fecha).getTime() - new Date(a.ultimo_aporte_fecha).getTime();
      });
    }
    return result;
  }, [análisis, estadoPago, rachaMinima, sinAportar, ordenarPor]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount);

  const formatUltimoAporte = (dateString?: string) => dateString ? formatDate(dateString) : 'Nunca';

  const getEstadoBadge = (aportioEsteMes: boolean) => {
    if (aportioEsteMes) {
      return <span className="px-2.5 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Aportó</span>;
    }
    return <span className="px-2.5 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">No aportó</span>;
  };

  const renderRacha = (racha: number) => {
    const label = racha === 1 ? '1 mes' : `${racha} meses`;
    if (racha >= 12) return (
      <span className="flex items-center gap-1">
        <Flame className="w-3.5 h-3.5 text-red-500 shrink-0" />
        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">{label}</span>
      </span>
    );
    if (racha >= 6) return (
      <span className="flex items-center gap-1">
        <Sparkles className="w-3.5 h-3.5 text-orange-500 shrink-0" />
        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">{label}</span>
      </span>
    );
    if (racha >= 3) return (
      <span className="flex items-center gap-1">
        <Sparkle className="w-3.5 h-3.5 text-yellow-500 shrink-0" />
        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">{label}</span>
      </span>
    );
    return (
      <span className="flex items-center gap-1">
        <CircleSmall className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-500">{label}</span>
      </span>
    );
  };

  const getModoCuotaBadge = (modo?: string) => {
    const label = modo?.includes('PA') ? 'PA' : 'JAL';
    return <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded">{label}</span>;
  };

  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">

      {/* Búsqueda + ordenar */}
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por nombre o RUT..."
            value={busqueda}
            onChange={(e) => handleBusquedaChange(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          />
        </div>
        <div className="shrink-0">
          <Select
            options={[
              { value: 'nombre', label: 'Nombre' },
              { value: 'racha', label: 'Racha' },
              { value: 'ultimo_aporte', label: 'Último aporte' },
            ]}
            value={ordenarPor}
            onChange={(v) => setOrdenarPor(v as 'nombre' | 'racha' | 'ultimo_aporte')}
            placeholder=""
          />
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {(['todos', 'al_dia', 'mora'] as const).map((val) => (
          <button
            key={val}
            type="button"
            onClick={() => setEstadoPago(val)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              estadoPago === val ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {ESTADO_PAGO_LABEL[val]}
          </button>
        ))}

        <div className="w-px h-5 bg-slate-200 mx-0.5" />

        <PillSelect
          placeholder="Racha"
          value={rachaMinima}
          onChange={setRachaMinima}
          options={[
            { value: '1', label: 'Con racha activa' },
            { value: '3', label: 'Racha 3+ meses' },
            { value: '6', label: 'Racha 6+ meses' },
            { value: '12', label: 'Racha 12+ meses' },
          ]}
        />

        <PillSelect
          placeholder="Sin aportar"
          value={sinAportar}
          onChange={setSinAportar}
          options={[
            { value: '1', label: '1+ mes' },
            { value: '2', label: '2+ meses' },
            { value: '3', label: '3+ meses' },
            { value: '6', label: '6+ meses' },
          ]}
        />
      </div>

      {/* Contador */}
      <div className="flex items-center gap-3 text-sm text-slate-600 mb-5">
        <span>Mostrando <strong className="text-slate-800">{análisisFiltrado.length}</strong> {análisisFiltrado.length === 1 ? 'miembro' : 'miembros'}</span>
        {hasActiveFilters && (
          <button type="button" onClick={resetFilters} className="flex items-center gap-1 text-slate-400 hover:text-slate-600 transition-colors">
            <X className="w-3.5 h-3.5" />
            Limpiar
          </button>
        )}
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700"></div>
        </div>
      )}
      {!loading && análisisFiltrado.length === 0 && (
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 mb-2">No hay datos de aportes</p>
          {hasActiveFilters && <p className="text-sm text-slate-500">Intenta cambiar los filtros</p>}
        </div>
      )}
      {!loading && análisisFiltrado.length > 0 && (
        <>
          {/* Fichas — mobile */}
          <div className="md:hidden grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
            {análisisFiltrado.map((aporte) => (
              <button
                key={aporte.miembro_id}
                type="button"
                onClick={() => openDrawer(aporte)}
                className="w-full text-left bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl p-4 transition-colors"
              >
                <div className="mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-800 text-sm leading-tight">
                      {aporte.nombres} {aporte.apellidos}
                    </p>
                    {getEstadoBadge(aporte.aportio_este_mes)}
                  </div>
                  {aporte.rut_pasaporte && (
                    <p className="text-xs text-slate-500 mt-0.5">{aporte.rut_pasaporte}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-3">
                  <p className="text-slate-500 font-medium">Racha</p>
                  <p className="text-slate-500 font-medium">Compromisos</p>
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-400 w-5 shrink-0">AP</span>
                    {renderRacha(aporte.racha_aporte_mensual)}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-400 w-5 shrink-0">AP</span>
                    <span className="font-medium text-slate-800">{formatCurrency(aporte.aporte_mensual)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-400 w-5 shrink-0">CI</span>
                    {renderRacha(aporte.racha_cuota_iglesia)}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-400 w-5 shrink-0">CI</span>
                    <span className="text-slate-700">{formatCurrency(aporte.aporte_cuota_iglesia)}</span>
                    {getModoCuotaBadge(aporte.modo_pago_cuota_iglesia)}
                  </div>
                </div>
                <div className="border-t border-slate-200 pt-2 text-xs text-slate-500">
                  Último aporte: <span className="font-medium text-slate-700">{formatUltimoAporte(aporte.ultimo_aporte_fecha)}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Tabla — desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Nombre</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Este mes</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Racha</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Último aporte</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Compromisos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {análisisFiltrado.map((aporte) => (
                  <tr
                    key={aporte.miembro_id}
                    className="hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => openDrawer(aporte)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openDrawer(aporte);
                      }
                    }}
                  >
                    <td className="px-4 py-4">
                      <p className="text-sm font-medium text-slate-800">{aporte.nombres} {aporte.apellidos}</p>
                      {aporte.rut_pasaporte && (
                        <p className="text-xs text-slate-500 mt-0.5">{aporte.rut_pasaporte}</p>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {getEstadoBadge(aporte.aportio_este_mes)}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-slate-400 w-5 shrink-0">AP</span>
                          {renderRacha(aporte.racha_aporte_mensual)}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-slate-400 w-5 shrink-0">CI</span>
                          {renderRacha(aporte.racha_cuota_iglesia)}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-600">
                      {formatUltimoAporte(aporte.ultimo_aporte_fecha)}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-slate-400 w-5 shrink-0">AP</span>
                          <span className="text-sm font-medium text-slate-800">{formatCurrency(aporte.aporte_mensual)}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-slate-400 w-5 shrink-0">CI</span>
                          <span className="text-sm text-slate-700">{formatCurrency(aporte.aporte_cuota_iglesia)}</span>
                          {getModoCuotaBadge(aporte.modo_pago_cuota_iglesia)}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {drawerMiembroData && (
        <AporteMiembroDrawer
          isOpen={!!drawerMiembro}
          onClose={closeDrawer}
          miembro={drawerMiembroData}
          lumisialId={lumisialId}
          puedeOperar={puedeOperar}
          onAporteModificado={() => onFilterChange({})}
        />
      )}
    </div>
  );
};
